import { useState } from "react";
import { Link } from "react-router-dom";
import { useAdminRuns } from "../../hooks/use-admin.js";
import { PageSkeleton } from "../../components/skeleton.js";

const LIMIT = 20;

function formatDuration(startedAt: string, completedAt: string | null): string {
  if (!completedAt) return "...";
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}m ${remaining}s`;
}

function stagesSummary(
  stages: Array<{ stage: string; status: string }>,
  runStatus: string,
): string {
  const total = stages.length;
  const done = stages.filter(
    (s) => s.status === "success" || s.status === "failed" || s.status === "skipped",
  ).length;
  if (done === total) return `${done}/${total} done`;
  const current = stages.find((s) => s.status === "running");
  if (current) return `${done}/${total} \u2014 ${current.stage}`;
  return `${done}/${total}`;
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case "success":
    case "completed":
      return "run-badge run-badge--success";
    case "running":
    case "in_progress":
      return "run-badge run-badge--running";
    case "failed":
    case "error":
      return "run-badge run-badge--failed";
    case "partial":
      return "run-badge run-badge--partial";
    default:
      return "run-badge run-badge--idle";
  }
}

export function AdminRunsPage() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useAdminRuns({ page, limit: LIMIT });

  if (isLoading) return <PageSkeleton />;

  return (
    <div className="product-page">
      <h1 className="page-title">Pipeline Executions</h1>
      {data && (
        <>
          <table className="admin-table text-sm">
            <thead>
              <tr>
                <th>Website</th>
                <th>Status</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Stages</th>
              </tr>
            </thead>
            <tbody>
              {data.runs.map((run) => (
                <tr key={run.executionId}>
                  <td>
                    <Link to={`/admin/websites/${run.websiteId}`}>
                      {run.websiteUrl}
                    </Link>
                  </td>
                  <td>
                    <span className={statusBadgeClass(run.status)}>
                      {run.status}
                    </span>
                  </td>
                  <td>{new Date(run.startedAt).toLocaleString("en-IE")}</td>
                  <td>{formatDuration(run.startedAt, run.completedAt)}</td>
                  <td>{stagesSummary(run.stages, run.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.runs.length === 0 && (
            <p className="text-muted text-center py-2">
              No pipeline runs found.
            </p>
          )}
          <div className="d-flex items-center gap-2 mt-3">
            <button
              className="btn btn--secondary btn--sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span className="text-sm">
              Page {page} &middot; {data.total} total
            </span>
            <button
              className="btn btn--secondary btn--sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={data.runs.length < LIMIT}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
